import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { EmptyState } from "../../components/Feedback";
import Card from "../../components/Card";
import Switch from "../../components/Switch";
import { ProjectCard } from "../../components/ProjectCard";
import BrandGuide from "../../components/BrandGuide";
import SocialMediaSettings from "../../components/SocialMediaSettings";
import { Badge, MISSION_STATUS, SERVICE_MODULES, fmtDate } from "../../lib/helpers";
import { ICONS_BY_NAME } from "../../lib/icons";
import { toast } from "sonner";
import * as Icons from "lucide-react";

// Page « cette entreprise × ce module » (décision de Mélilia du 25/10). Elle manquait, et c'est son
// absence qui déformait le reste : un module n'avait qu'une page de catalogue, tous clients
// confondus (/admin/modules/<clé>), et la fiche entreprise n'y renvoyait jamais. Les réglages d'un
// module s'étaient donc installés au rang d'onglets de la fiche — « Réseaux » voisinait avec
// « Modules », alors qu'il EST le réglage d'un module.
//
// Ce qu'on y trouve : les projets de ce module pour cette entreprise, les missions qui en dépendent,
// et les réglages propres au module. Pour Identité Visuelle, la charte y figure — sans quitter la
// fiche pour autant : côté agence elle reste toujours atteignable, c'est un outil de production
// (décision du 25/10). Un seul composant partagé, deux points de montage.

// Les réglages dépendent du module. Un module sans entrée ici n'a pas encore de réglage propre :
// la page montre ses projets et ses missions, et c'est déjà ce qu'on venait chercher.
const REGLAGES = { reseaux_sociaux: SocialMediaSettings, identite_visuelle: BrandGuide };

export default function AdminClientModule() {
  const { id, key } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => api.get(`/admin/clients/${id}`)
    .then((r) => { setData(r.data); setLoadError(null); })
    .catch((e) => setLoadError([403, 404].includes(e?.response?.status) ? "missing" : "failed")), [id]);

  useEffect(() => { load(); api.get("/admin/modules").then((r) => setModules(r.data)).catch(() => setModules([])); }, [id, load]);

  // `/admin/modules` fait foi pour le libellé : il contient aussi les modules créés à la main, que
  // SERVICE_MODULES ne connaît pas. Ce dernier ne sert que de secours.
  const module = useMemo(() => (modules || []).find((m) => m.key === key), [modules, key]);
  const label = module?.label || SERVICE_MODULES[key]?.label || key;
  const icone = module?.icon || SERVICE_MODULES[key]?.icon || "LayoutGrid";

  const projets = useMemo(() => (data?.projects || []).filter((p) => p.type === key), [data, key]);
  const missions = useMemo(() => {
    const pids = new Set(projets.map((p) => p.id));
    return (data?.missions || []).filter((m) => pids.has(m.project_id));
  }, [data, projets]);

  if (!data && loadError) return (
    <EmptyState icon="FolderX" title={loadError === "missing" ? "Entreprise introuvable" : "Impossible d'afficher ce module"}
      hint={loadError === "missing" ? "Elle a peut-être été effacée." : "Vérifiez votre connexion, puis rechargez la page."}
      action={<button onClick={() => nav("/admin/clients")} className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors">Retour aux entreprises</button>} />
  );
  if (!data) return null;

  const c = data.client;
  const actifs = c.active_modules || [];
  const actif = actifs.includes(key);
  const Reglages = REGLAGES[key];

  const toggle = async () => {
    const next = actif ? actifs.filter((x) => x !== key) : [...actifs, key];
    try {
      await api.put(`/admin/clients/${id}`, { active_modules: next });
      toast.success(actif ? `${label} désactivé pour cette entreprise` : `${label} activé pour cette entreprise`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
  };

  // Jamais Icons[nom] : un accès par nom sur toute la bibliothèque y réembarque les ~1 800
  // icônes (+125 ko compressés). Un nom de module nouveau s'ajoute à ICONS_BY_NAME.
  const Icone = ICONS_BY_NAME[icone] || ICONS_BY_NAME.LayoutGrid;

  return (
    <div data-testid="client-module-page">
      <button onClick={() => nav(`/admin/clients/${id}?vue=modules`)} data-testid="module-back"
        className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-3">
        <Icons.ArrowLeft size={16} className="text-marine" /> Retour à {c.name}
      </button>

      <PageTitle overline={c.name} title={label}
        action={
          <div className="flex items-center gap-3" data-testid="module-activation">
            <span className="text-sm text-marine/80">{actif ? "Souscrit" : "Non souscrit"}</span>
            <Switch checked={actif} onChange={toggle} testid="module-toggle"
              title={actif ? `Retirer ${label} à ${c.name}` : `Donner ${label} à ${c.name}`} />
          </div>
        } />

      <div className="space-y-6">
        {!actif && (
          <Card title="Module non souscrit">
            <p className="text-sm text-marine/80">
              <Icone size={16} className="inline mr-1.5 text-marine" />
              {c.name} n'a pas souscrit {label}. Vous pouvez consulter et préparer ce qui s'y rattache ;
              l'entreprise n'y verra rien dans son espace tant que le module n'est pas activé.
            </p>
          </Card>
        )}

        <Card title="Projets" action={<span className="text-xs text-marine/80">{projets.length} projet{projets.length > 1 ? "s" : ""}</span>}>
          {projets.length === 0 ? (
            <p className="text-sm text-marine/80">Aucun projet de ce module pour cette entreprise. Les projets se créent depuis sa fiche ou depuis la page du module.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="module-projects">
              {projets.map((p) => (
                <ProjectCard key={p.id} project={p} clientName={() => c.name} onOpen={(x) => nav(`/admin/projets/${x.id}`)} />
              ))}
            </div>
          )}
        </Card>

        <Card title="Missions" action={<span className="text-xs text-marine/80">{missions.length} mission{missions.length > 1 ? "s" : ""}</span>}>
          {missions.length === 0 ? (
            <p className="text-sm text-marine/80">Aucune mission rattachée aux projets de ce module.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2" data-testid="module-missions">
              {missions.map((m) => (
                <button key={m.id} onClick={() => nav(`/admin/collaborateurs/${m.collaborator_id}`)} data-testid={`module-mission-${m.id}`}
                  className="border border-marine/10 rounded-sm p-3 flex items-center justify-between gap-3 hover:border-marine/40 transition-colors text-left">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-marine truncate">{m.type}</div>
                    <div className="text-xs text-marine/80">{m.collaborator_name}{m.project_name ? ` · ${m.project_name}` : ""}{m.deadline ? ` · Deadline ${fmtDate(m.deadline)}` : ""}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0"><Badge status={m.status} map={MISSION_STATUS} /><Icons.ChevronRight size={16} className="text-marine" /></div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {Reglages && <Reglages clientId={id} client={c} onSaved={load} />}
      </div>
    </div>
  );
}
